import { useAuth0 } from '@auth0/auth0-react'
import { getCurrentEnvironmentName } from '@sparkai/env'
import { ReactNode, useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom-v5-compat'

import { getReturnToUrl } from '../utils/getReturnToUrl'
import { LoadingMessage } from './LoadingMessage'

export interface RedirectPageProps {
  children?: ReactNode
}

export function RedirectPage(_: RedirectPageProps) {
  const { error, loginWithRedirect, isAuthenticated, isLoading } = useAuth0()
  const location = useLocation()

  const errorDescription = new URLSearchParams(location.search).get('error_description')

  const retryLogin = useCallback(async () => {
    await loginWithRedirect({
      prompt: 'login',
      appState: { returnTo: getReturnToUrl() },
      'ext-is-sandbox': isOnAnSandboxEnvironment ? 'true' : 'false',
    })
  }, [loginWithRedirect])

  /**
   * This prevents a dead end in the login flow
   * caused by having a password reset before the login.
   */
  useEffect(() => {
    if (!isAuthenticated && !isLoading && !error) {
      retryLogin()
    }
  }, [error, isAuthenticated, isLoading, retryLogin])

  if (error)
    return (
      <div className='flex h-screen w-full flex-col items-center justify-center bg-brand-gray-50'>
        <h1 className='font-inter text-2xl font-bold text-brand-gray-900'>Failed to authenticate</h1>

        <p>{errorDescription ? errorDescription : 'Something went wrong when authenticating'}</p>
        <div className='mt-4'>
          <Button onClick={retryLogin}>Retry Login</Button>
        </div>
      </div>
    )

  return <LoadingMessage />
}

const Button = ({ children, onClick }: { children: ReactNode; onClick?: () => void }) => {
  return (
    <button
      onClick={onClick}
      type='button'
      className='px-auto py-auto inline-flex min-w-[94px]  items-center rounded-md border border-transparent bg-brand-blue-600 px-4 py-2 font-inter text-sm font-medium  text-white shadow-sm hover:bg-brand-blue-700 focus:outline-none focus:ring-2 focus:ring-brand-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-brand-blue-300'
    >
      <span className='mx-auto w-full'>{children}</span>
    </button>
  )
}

const isOnAnSandboxEnvironment = getCurrentEnvironmentName().includes('sandbox')
